import type * as type from "@vTypes";
import * as alarm from "@script/alarm";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ChevronRight } from "lucide-react";
import { toast } from "@/components/toast/CustomToast";
import LoadingSpiner from "@/components/ui/LoadingSpiner";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import HeaderAlarmModal from "@/layouts/components/HeaderAlarmModal";

function getTitleKey(message: string): string {
  const match = message.match(
    /^(\d{4}년\s*\d{1,2}월\s*(?:근무표|원티드\s*요청))/
  );
  return match ? match[1].trim() : message;
}

function parseAlarmLink(item: type.AlarmItem): string | null {
  const { pushsubcode, Message } = item;
  const yearMatch = Message.match(/(\d{4})년/);
  const monthMatch = Message.match(/(\d{1,2})월/);
  if (!yearMatch || !monthMatch) return null;

  const year = parseInt(yearMatch[1], 10);
  const month = parseInt(monthMatch[1], 10);

  switch (pushsubcode) {
    case "S01":
    case "S04":
      return `/roster_view?year=${year}&month=${month}`;
    case "S02":
    case "S03":
    case "S05":
      return `/roster_wanted?year=${year}&month=${month}`;
    default:
      return null;
  }
}

type Props = {
  isAuthenticated: boolean;
};

type UnreadCountRow = { PushCode: string; PushCnt: number };

type AlarmOptimisticContext = {
  previousPopover?: type.AlarmItem[];
  previousModal?: type.AlarmItem[];
  previousCount?: UnreadCountRow[];
};

function markAlarmReadByFk(
  list: type.AlarmItem[] | undefined,
  fkIdx: number
): type.AlarmItem[] | undefined {
  if (list == null) return list;
  return list.map((item) =>
    item.fk_idx === fkIdx && item.ReadYN === "N"
      ? { ...item, ReadYN: "Y" as const }
      : item
  );
}

function markGroupReadInList(
  list: type.AlarmItem[] | undefined,
  groupItems: type.AlarmItem[]
): type.AlarmItem[] | undefined {
  if (list == null) return list;
  const fkToMark = new Set(
    groupItems
      .filter((i) => i.ReadYN === "N" && i.fk_idx != null)
      .map((i) => i.fk_idx as number)
  );
  if (fkToMark.size === 0) return list;
  return list.map((item) =>
    item.fk_idx != null && fkToMark.has(item.fk_idx) && item.ReadYN === "N"
      ? { ...item, ReadYN: "Y" as const }
      : item
  );
}

function markAllReadInList(
  list: type.AlarmItem[] | undefined
): type.AlarmItem[] | undefined {
  if (list == null) return list;
  return list.map((item) =>
    item.ReadYN === "N" ? { ...item, ReadYN: "Y" as const } : item
  );
}

function zeroUnreadCount(prev: UnreadCountRow[] | undefined): UnreadCountRow[] {
  if (!prev?.length) return [];
  return prev.map((r) => ({ ...r, PushCnt: 0 }));
}

/** 집계 행에서 미읽음 총합을 `delta`만큼 줄임(표시용) */
function decrementUnreadCountBy(
  prev: UnreadCountRow[] | undefined,
  delta: number
): UnreadCountRow[] {
  if (!prev?.length || delta <= 0) return prev ?? [];
  let remaining = delta;
  const copy = prev.map((r) => ({ ...r }));
  for (const row of copy) {
    if (remaining <= 0) break;
    if (row.PushCnt > 0) {
      const take = Math.min(row.PushCnt, remaining);
      row.PushCnt -= take;
      remaining -= take;
    }
  }
  return copy;
}

function rollbackAlarmCaches(
  queryClient: QueryClient,
  ctx: AlarmOptimisticContext | undefined
) {
  if (ctx?.previousPopover !== undefined) {
    queryClient.setQueryData(
      QUERY_KEYS.alarms.listPopover(),
      ctx.previousPopover
    );
  }
  if (ctx?.previousModal !== undefined) {
    queryClient.setQueryData(QUERY_KEYS.alarms.listModal(), ctx.previousModal);
  }
  if (ctx?.previousCount !== undefined) {
    queryClient.setQueryData(QUERY_KEYS.alarms.count(), ctx.previousCount);
  }
}

export default function HeaderAlarmSection({ isAuthenticated }: Props) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isAllNotificationsModalOpen, setIsAllNotificationsModalOpen] =
    useState(false);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const { data: unreadCountData = [] } = useQuery<
    { PushCode: string; PushCnt: number }[]
  >({
    queryKey: QUERY_KEYS.alarms.count(),
    queryFn: () => alarm.listcnt(),
    enabled: isAuthenticated,
    staleTime: 30 * 1000,
    // refetchInterval: 30 * 1000,
  });

  const unreadCount = unreadCountData.reduce(
    (sum, item) => sum + item.PushCnt,
    0
  );

  const { data: alarmListForPopover = [], isPending: isPopoverLoading } =
    useQuery<type.AlarmItem[]>({
      queryKey: QUERY_KEYS.alarms.listPopover(),
      queryFn: () => alarm.list(20),
      enabled: isAuthenticated && isNotificationOpen,
      staleTime: 60 * 1000,
    });

  const { data: alarmListForModal = [], isPending: isModalLoading } = useQuery<
    type.AlarmItem[]
  >({
    queryKey: QUERY_KEYS.alarms.listModal(),
    queryFn: () => alarm.list(50),
    enabled: isAuthenticated && isAllNotificationsModalOpen,
    staleTime: 60 * 1000,
  });

  const recentAlarmList = alarmListForPopover.slice(0, 3);
  const hasMoreAlarms = alarmListForPopover.length > 3;
  const filteredAlarmList = showUnreadOnly
    ? alarmListForModal.filter((item) => item.ReadYN === "N")
    : alarmListForModal;

  const { mutate: markAllAsReadMutate, isPending: isMarkingAsRead } =
    useMutation({
      mutationFn: async () => await alarm.markAllAsRead(),
      onMutate: async (): Promise<AlarmOptimisticContext> => {
        await queryClient.cancelQueries({
          queryKey: QUERY_KEYS.alarms.listPopover(),
        });
        await queryClient.cancelQueries({
          queryKey: QUERY_KEYS.alarms.listModal(),
        });
        await queryClient.cancelQueries({
          queryKey: QUERY_KEYS.alarms.count(),
        });

        const previousPopover = queryClient.getQueryData<type.AlarmItem[]>(
          QUERY_KEYS.alarms.listPopover()
        );
        const previousModal = queryClient.getQueryData<type.AlarmItem[]>(
          QUERY_KEYS.alarms.listModal()
        );
        const previousCount = queryClient.getQueryData<UnreadCountRow[]>(
          QUERY_KEYS.alarms.count()
        );

        queryClient.setQueryData(
          QUERY_KEYS.alarms.listPopover(),
          (old: type.AlarmItem[] | undefined) => markAllReadInList(old)
        );
        queryClient.setQueryData(
          QUERY_KEYS.alarms.listModal(),
          (old: type.AlarmItem[] | undefined) => markAllReadInList(old)
        );
        queryClient.setQueryData(
          QUERY_KEYS.alarms.count(),
          (old: UnreadCountRow[] | undefined) => zeroUnreadCount(old)
        );

        return { previousPopover, previousModal, previousCount };
      },
      onError: (error, _vars, context) => {
        console.error("전체 읽음 처리 실패:", error);
        rollbackAlarmCaches(queryClient, context);
        toast.error("전체 읽음 처리에 실패했습니다.");
      },
      onSuccess: () => {
        setShowUnreadOnly(false);
        toast.success("알람이 읽음처리되었습니다.");
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.alarms.list() });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.alarms.count() });
      },
    });

  const { mutate: markOneReadAndNavigateMutate } = useMutation({
    mutationFn: async (fkIdx: number) => await alarm.markOneRead(fkIdx),
    onMutate: async (fkIdx): Promise<AlarmOptimisticContext> => {
      await queryClient.cancelQueries({
        queryKey: QUERY_KEYS.alarms.listPopover(),
      });
      await queryClient.cancelQueries({
        queryKey: QUERY_KEYS.alarms.listModal(),
      });
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.alarms.count() });

      const previousPopover = queryClient.getQueryData<type.AlarmItem[]>(
        QUERY_KEYS.alarms.listPopover()
      );
      const previousModal = queryClient.getQueryData<type.AlarmItem[]>(
        QUERY_KEYS.alarms.listModal()
      );
      const previousCount = queryClient.getQueryData<UnreadCountRow[]>(
        QUERY_KEYS.alarms.count()
      );

      const wasUnread =
        Boolean(
          previousPopover?.some((i) => i.fk_idx === fkIdx && i.ReadYN === "N")
        ) ||
        Boolean(
          previousModal?.some((i) => i.fk_idx === fkIdx && i.ReadYN === "N")
        );

      queryClient.setQueryData(
        QUERY_KEYS.alarms.listPopover(),
        (old: type.AlarmItem[] | undefined) => markAlarmReadByFk(old, fkIdx)
      );
      queryClient.setQueryData(
        QUERY_KEYS.alarms.listModal(),
        (old: type.AlarmItem[] | undefined) => markAlarmReadByFk(old, fkIdx)
      );
      if (wasUnread) {
        queryClient.setQueryData(
          QUERY_KEYS.alarms.count(),
          (old: UnreadCountRow[] | undefined) => decrementUnreadCountBy(old, 1)
        );
      }

      return { previousPopover, previousModal, previousCount };
    },
    onError: (error, _fkIdx, context) => {
      console.error("읽음 처리 실패:", error);
      rollbackAlarmCaches(queryClient, context);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.alarms.list() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.alarms.count() });
    },
  });

  const { mutate: markGroupAsReadMutate } = useMutation({
    mutationFn: async (groupItems: type.AlarmItem[]) => {
      const unreadItems = groupItems.filter(
        (item) => item.ReadYN === "N" && item.fk_idx
      );
      await Promise.all(
        unreadItems.map((item) => alarm.markOneRead(item.fk_idx!))
      );
    },
    onMutate: async (groupItems): Promise<AlarmOptimisticContext> => {
      await queryClient.cancelQueries({
        queryKey: QUERY_KEYS.alarms.listPopover(),
      });
      await queryClient.cancelQueries({
        queryKey: QUERY_KEYS.alarms.listModal(),
      });
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.alarms.count() });

      const previousPopover = queryClient.getQueryData<type.AlarmItem[]>(
        QUERY_KEYS.alarms.listPopover()
      );
      const previousModal = queryClient.getQueryData<type.AlarmItem[]>(
        QUERY_KEYS.alarms.listModal()
      );
      const previousCount = queryClient.getQueryData<UnreadCountRow[]>(
        QUERY_KEYS.alarms.count()
      );

      const unreadItems = groupItems.filter(
        (item) => item.ReadYN === "N" && item.fk_idx
      );
      const delta = unreadItems.length;

      queryClient.setQueryData(
        QUERY_KEYS.alarms.listPopover(),
        (old: type.AlarmItem[] | undefined) =>
          markGroupReadInList(old, groupItems)
      );
      queryClient.setQueryData(
        QUERY_KEYS.alarms.listModal(),
        (old: type.AlarmItem[] | undefined) =>
          markGroupReadInList(old, groupItems)
      );
      if (delta > 0) {
        queryClient.setQueryData(
          QUERY_KEYS.alarms.count(),
          (old: UnreadCountRow[] | undefined) =>
            decrementUnreadCountBy(old, delta)
        );
      }

      return { previousPopover, previousModal, previousCount };
    },
    onError: (error, _groupItems, context) => {
      console.error("그룹 읽음 처리 실패:", error);
      rollbackAlarmCaches(queryClient, context);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.alarms.list() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.alarms.count() });
    },
  });

  const handleAlarmClick = (
    item: type.AlarmItem,
    groupItems?: type.AlarmItem[]
  ) => {
    const link = parseAlarmLink(item);

    if (groupItems && groupItems.length > 0) {
      markGroupAsReadMutate(groupItems);
      if (link) {
        navigate(link);
        setIsNotificationOpen(false);
        setIsAllNotificationsModalOpen(false);
      }
      return;
    }

    if (item.ReadYN === "N" && item.fk_idx) {
      markOneReadAndNavigateMutate(item.fk_idx);
    }

    if (link) {
      navigate(link);
      setIsNotificationOpen(false);
      setIsAllNotificationsModalOpen(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <>
      <li className="fw-600">
        <Popover open={isNotificationOpen} onOpenChange={setIsNotificationOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={`c_userMenu__notice relative ${
                unreadCount > 0 ? "c_userMenu__notice--newMessage" : ""
              }`}
            >
              <span className="c_userMenu__notice_bellIcon relative">
                {unreadCount > 0 && (
                  <>
                    <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[18px] h-[18px] px-1.5 text-[10px] font-bold text-white bg-red-500 rounded-full animate-ping opacity-75 z-10">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                    <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[18px] h-[18px] px-1.5 text-[10px] font-bold text-white bg-red-500 rounded-full z-10">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  </>
                )}
              </span>
              <span>알림</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="end" sideOffset={8}>
            <div className="flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <h3
                  className="font-semibold group text-base cursor-pointer hover:text-sig transition-colors flex items-center gap-1"
                  onClick={() => {
                    setIsNotificationOpen(false);
                    setIsAllNotificationsModalOpen(true);
                  }}
                >
                  알림
                  <ChevronRight className="w-4 h-4 opacity-30 group-hover:opacity-60" />
                </h3>
                <button
                  onClick={() => setIsNotificationOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="max-h-[400px] overflow-y-auto">
                {isPopoverLoading ? (
                  <div className="px-4 py-8 flex items-center justify-center">
                    <LoadingSpiner />
                  </div>
                ) : recentAlarmList.length === 0 ? (
                  <div className="px-4 py-8 text-center text-gray-500">
                    알림이 없습니다.
                  </div>
                ) : (
                  (() => {
                    const groupedAlarms = new Map<string, type.AlarmItem[]>();
                    alarmListForPopover.forEach((item) => {
                      const titleKey = getTitleKey(item.Message);
                      if (!groupedAlarms.has(titleKey))
                        groupedAlarms.set(titleKey, []);
                      groupedAlarms.get(titleKey)!.push(item);
                    });

                    return (
                      <div className="divide-y">
                        {recentAlarmList.map((item, index) => {
                          const titleKey = getTitleKey(item.Message);
                          const group = groupedAlarms.get(titleKey);
                          if (!group || group.length === 0) return null;

                          const latestItem = group[0];
                          const isLatestInGroup = latestItem.fk_idx
                            ? latestItem.fk_idx === item.fk_idx
                            : latestItem.regdate === item.regdate &&
                              latestItem.Message === item.Message;

                          const link = isLatestInGroup
                            ? parseAlarmLink(item)
                            : null;
                          const isClickable = link !== null;

                          return (
                            <div
                              key={index}
                              onClick={() =>
                                isClickable && handleAlarmClick(item, group)
                              }
                              className={`px-4 py-3 transition-colors ${
                                item.ReadYN === "N" ? "bg-blue-50/30" : ""
                              } ${isClickable ? "hover:bg-gray-50 cursor-pointer" : ""}`}
                            >
                              <div className="flex items-start gap-3">
                                <div className="flex-1 min-w-0">
                                  <p
                                    className={`text-sm ${
                                      item.ReadYN === "N"
                                        ? "font-semibold text-sig"
                                        : "font-medium text-gray-900"
                                    }`}
                                  >
                                    {item.Message}
                                  </p>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className="text-xs text-gray-500">
                                      {item.regdate.replaceAll("-", ".")}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()
                )}
              </div>

              {hasMoreAlarms && (
                <div className="border-t px-4 py-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsNotificationOpen(false);
                      setIsAllNotificationsModalOpen(true);
                    }}
                    className="w-full text-center text-sm! text-zinc-500 hover:text-zinc-800 font-medium"
                  >
                    더보기
                  </button>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </li>

      {/* Modal */}
      <HeaderAlarmModal
        isOpen={isAllNotificationsModalOpen}
        onClose={() => {
          setIsAllNotificationsModalOpen(false);
          setShowUnreadOnly(false);
        }}
        showUnreadOnly={showUnreadOnly}
        setShowUnreadOnly={setShowUnreadOnly}
        unreadCount={unreadCount}
        isMarkingAsRead={isMarkingAsRead}
        onMarkAllAsRead={() => markAllAsReadMutate()}
        isModalLoading={isModalLoading}
        filteredAlarmList={filteredAlarmList}
        parseAlarmLink={parseAlarmLink}
        onAlarmClick={(item) => handleAlarmClick(item)}
      />
    </>
  );
}
